import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, NgControl } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { emailValidator, filterBoolean } from '@recruitee/common';
import { RecipientTypehead } from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';

import { ButtonComponent } from '../../common/button/components/button/button.component';
import { ComponentVariant, Variants } from '../../common/variant/variant';
import { KeyCode } from '../../core/keycode';
import { ConnectedOverlayDirective } from '../../core/overlay/overlay-directives';
import { NotificationRef, NotificationsController } from '../../notifications/notifications.module';
import { InputComponent } from '../input/input.component';
import { SuggestionsContext, TypeaheadComponent } from '../typeahead/typeahead.component';
import { Suggestion, TypeaheadSuggestionsState } from '../typeahead/typeahead.service';

/**
 * TODO: This components seems to be broken in many ways
 * It doesn't really support reactive forms and is pain to use with regular model update mechanism
 * Also TaggerTag interface is heavily coupled with app logic,
 */

export type TaggerTag = {
  candidate?: {};
  admin?: {};
  candidateId?: number;
  email: string;
  name?: string;
  type?: string;
  isValid?: boolean;
  error?: string;
};

export type TaggerSuggestion = {
  id: string;
  value: string;
  kind: string;
  emailObject: TaggerTag;
};

export interface TypeaheadSuggestionsContext<S = Suggestion> {
  state: SuggestionsContext<S>['typeaheadstate'];
  typeahead: TypeaheadComponent;
}

@Component({
  selector: 'rt-tagger',
  templateUrl: './tagger.component.html',
  styleUrls: ['./tagger.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tagger',
    '[class]': 'variant',
  },
})
export class TaggerComponent {
  public static readonly VARIANTS: Variants = ['default', ['default', 'email']];
  private static SPACE_KEY_CODE: number = 32;
  private static ENTER_KEY_CODE: number = 13;
  private static COMMA_KEY_CODE: number = 188;

  public readonly SuggestionsContext: SuggestionsContext<any>;
  public readonly DefaultTypeaheadSuggestionsContext: TypeaheadSuggestionsContext;
  public readonly EmailTypeaheadSuggestionsContext: TypeaheadSuggestionsContext<RecipientTypehead>;

  @ComponentVariant(TaggerComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input()
  public get tags(): TaggerTag[] {
    return this._tags;
  }

  // TODO: This component uses input mutation in multiple places. It should communicate changes
  // either by updating the passed `control` or by emitting `tagsChange` event.
  public set tags(value: TaggerTag[]) {
    if (value !== this._tags) {
      this._tags = value;
      this.tagsChange.emit(this._tags);
      this.control.setValue(this._tags);
    }
  }

  @Input() public placeholder: string = '';
  @Input()
  public delimiterKeyCodes: number[] = [
    TaggerComponent.ENTER_KEY_CODE,
    TaggerComponent.COMMA_KEY_CODE,
  ];
  @Input() public tagValidator: (control: AbstractControl) => string | null =
    this.defaultTagValidator;
  @Input() public tagTransformation: (
    tag: TaggerTag | TaggerSuggestion | string,
    isValid: boolean,
    error?: string,
  ) => { email: string; isValid: boolean } | TaggerTag = this.defaultTagTransformation;
  @Input() public tagTypeFn: (
    tag: TaggerTag,
  ) => 'admin' | 'candidate' | 'email' | 'default' | void = this.defaultTagTypeFn;
  @Input() public tagTitleFn: (tag: TaggerTag) => string | TaggerTag = this.defaultTagTitleFn;
  @Input() public typeaheadSuggestions: TaggerSuggestion[] = [];
  @Input() public typeaheadSuggestionsTemplate: TemplateRef<TypeaheadSuggestionsContext<any>>;
  @Input() public singleTagTemplate: TemplateRef<any>;
  @Input() public control: UntypedFormControl = new UntypedFormControl();
  @Input() public disabled: boolean = false;
  @Input() public enabledEmailChange: boolean = true;
  @Input() public selectCandidate: boolean = false;
  @Input() public markInvalid: boolean = false;
  @Input() public preserveWhiteSpaces: boolean = false;
  @Input() public applyStyleOnFocus: boolean = false;

  @Output() public firstEmail: EventEmitter<string | null> = new EventEmitter();
  @Output() public openCandidate: EventEmitter<{ id: number }> = new EventEmitter();
  @Output() public typeaheadChanged: EventEmitter<string> = new EventEmitter();
  @Output() public tagsChange: EventEmitter<TaggerTag[]> = new EventEmitter();
  @Output() public deleteLastTag: EventEmitter<void> = new EventEmitter();

  @ViewChild('input', { read: NgControl, static: true }) public input: NgControl;
  @ViewChild('input') public inputCmp: InputComponent;
  @ViewChild('defaultSingleTagTemplate')
  public defaultSingleTagTemplate: TemplateRef<any>;
  @ViewChild('emailSingleTagTemplate', { static: true })
  public emailSingleTagTemplate: TemplateRef<any>;
  @ViewChild('defaultSuggestionsTemplate', { static: true })
  public defaultSuggestionsTemplate: TemplateRef<TypeaheadSuggestionsContext<any>>;
  @ViewChild('emailSuggestionsTemplate', { static: true })
  public emailSuggestionsTemplate: TemplateRef<TypeaheadSuggestionsContext<any>>;
  @ViewChild('taggerTypeahead', { static: true }) public taggerTypeahead: TypeaheadComponent;
  @ViewChildren('tagButton') public tagButtons: QueryList<ButtonComponent>;

  public dummyInputControl: UntypedFormControl = new UntypedFormControl();
  public readonly dropdownConfig: any = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];
  public hasInvalidTags: boolean = false;
  public hasFocusActive: boolean = false;

  private typeaheadSugestionsState: TypeaheadSuggestionsState;
  private errorNotification: NotificationRef | null;
  private _tags: TaggerTag[];
  private error$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private i18n: I18nService,
    private notificationsCtrl: NotificationsController,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.control.setValue(this.tags);
  }

  public ngAfterViewInit(): void {
    this.taggerTypeahead.data$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(state => (this.typeaheadSugestionsState = state));

    this.error$
      .pipe(filterBoolean, debounceTime(200), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(error => {
        this.showError(error!);
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes) {
      changes.disabled.currentValue
        ? this.dummyInputControl.disable()
        : this.dummyInputControl.enable();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggleDropdown(dropdown: ConnectedOverlayDirective, state?: boolean): void {
    if (state === undefined) {
      dropdown.open = !dropdown.open;
    } else {
      dropdown.open = state;
    }
    this.cdRef.detectChanges();
  }

  public trackByTag<T extends { name?: string }>(index: number, { name }: T): T['name'] {
    return name;
  }

  public trackBySuggestion<T extends { id: any }>(index: number, { id }: T): T['id'] {
    return id;
  }

  public onInput(): void {
    if (!this._tags.length && this.isVariant('email')) {
      const error =
        typeof this.tagValidator === 'function'
          ? this.tagValidator(this.input.control!)
          : this.defaultTagValidator(this.input.control!);

      !error ? this.firstEmail.next(this.input.value) : this.firstEmail.next(null);
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.keyCode === KeyCode.Backspace && (!this.input.value || !this.input.value.length))
      this.deleteLastTag.emit();

    if (this.errorNotification) {
      this.errorNotification.close();
      this.errorNotification.afterClosed.subscribe(() => (this.errorNotification = null));
    }

    if (
      event.keyCode === TaggerComponent.SPACE_KEY_CODE &&
      this.isVariant('email') &&
      this.input.value.indexOf('@') > 0
    ) {
      event.preventDefault();
      this.tryAddTag();
    }

    if (this.delimiterKeyCodes.indexOf(event.keyCode) === -1) {
      return;
    }
    if (
      event.keyCode === TaggerComponent.ENTER_KEY_CODE &&
      this.typeaheadSugestionsState.highlighted
    ) {
      return;
    }
    event.preventDefault();
    this.tryAddTag();
  }

  public onBlur(): void {
    this.tryAddTag();
    this.hasFocusActive = false;
    this.cdRef.detectChanges();
  }

  public onFocus(): void {
    this.hasFocusActive = true;
    this.cdRef.detectChanges();
  }

  public deleteTag(tag: TaggerTag, index: number): void {
    if (this.disabled) return;
    this.tags = this.tags.slice(0, index).concat(this.tags.slice(index + 1));
    this.hasInvalidTags = this.checkInvalidTags();
    this.inputCmp.setFocus();
    this.cdRef.detectChanges();
  }

  public handleClickOnTagger(event: MouseEvent): void {
    const clickedOnSingleTag = this.tagButtons.some(tagButton =>
      tagButton.elRef.nativeElement.contains(event.target),
    );

    if (!clickedOnSingleTag) {
      // @ts-ignore
      this.input.valueAccessor.setFocus();
    }
  }

  public selectedWithTypeahead(suggestion: TaggerSuggestion): void {
    this.newTag(suggestion);
    this.input.control!.setValue('');
  }

  public onEmailSelect({ index, email }: { index: number; email: string }): void {
    this.tags = this.tags.map((tag, i) => (i === index ? { ...tag, email } : tag));
  }

  public hasDropdown(tag: TaggerTag, index: number): boolean {
    return this.enabledEmailChange && (!!this.tags[index].candidate || !!this.tags[index].admin);
  }

  public get defaultTemplate(): TemplateRef<any> {
    return this.isVariant('email') ? this.emailSingleTagTemplate : this.defaultSingleTagTemplate;
  }

  public get suggestionsTemplate(): TemplateRef<TypeaheadSuggestionsContext<any>> {
    if (this.typeaheadSuggestionsTemplate) {
      return this.typeaheadSuggestionsTemplate;
    } else if (this.isVariant('email')) {
      return this.emailSuggestionsTemplate;
    } else {
      return this.defaultSuggestionsTemplate;
    }
  }

  private checkInvalidTags(): boolean {
    if (!this.markInvalid || !Array.isArray(this.tags)) {
      return false;
    }
    return this.tags.filter(tag => !tag.isValid).length > 0;
  }

  private clearInput(): void {
    this.input.control!.setValue('');
  }

  private defaultTagValidator(control: AbstractControl): string | null {
    if (this.isVariant('email') && emailValidator(<UntypedFormControl>control)) {
      return this.i18n.translate('rtu.common.invalid_email');
    } else {
      return null;
    }
  }

  private defaultTagTransformation(
    tag: TaggerTag | TaggerSuggestion | string,
    isValid: boolean,
    error?: string,
  ): { email: string; isValid: boolean } | TaggerTag {
    if (this.isVariant('email')) {
      if (typeof tag === 'string') {
        return { email: tag, isValid, ...(error && { error }) };
      } else {
        // @ts-ignore
        return tag.emailObject;
      }
    } else {
      // @ts-ignore
      return tag;
    }
  }

  private defaultTagTypeFn(tag: TaggerTag): 'admin' | 'candidate' | 'email' | 'default' | void {
    if (this.isVariant('email')) {
      if (tag.admin) {
        return 'admin';
      } else if (tag.candidate) {
        return 'candidate';
      } else if (tag.email) {
        return 'email';
      }
    } else {
      // eslint-disable-next-line max-lines
      return 'default';
    }
  }

  private defaultTagTitleFn(tag: TaggerTag): string | TaggerTag {
    if (this.isVariant('email')) {
      if (tag.name) {
        return `${tag.name} (${tag.email})`;
      } else {
        return tag.email;
      }
    } else {
      return tag;
    }
  }

  private tryAddTag(): void {
    const safeValue = (this.input.value || '').trim();
    const parsedInput = safeValue
      .replace(';', ',')
      .replace(/\s/g, this.preserveWhiteSpaces ? ',' : '');

    parsedInput.split(',').forEach((value: any) => {
      this.input.control!.setValue(value);
      if (value.length === 0) {
        return;
      }
      const error =
        typeof this.tagValidator === 'function'
          ? this.tagValidator(this.input.control!)
          : this.defaultTagValidator(this.input.control!);

      if (this.markInvalid) {
        this.newTag(value, !error, error || undefined);
      } else {
        if (error !== null) {
          return this.error$.next(error);
        }
        this.newTag(value);
      }
    });

    this.clearInput();
  }

  private showError(message: string): void {
    this.errorNotification = this.notificationsCtrl.error(message, {
      showClose: true,
      timeout: 5000,
    });
  }

  private newTag(tag: TaggerTag | TaggerSuggestion, isValid: boolean = true, error?: string): void {
    this.tags = [...this.tags, this.tagTransformation(tag, isValid, error)];
    this.hasInvalidTags = this.checkInvalidTags();
    this.cdRef.detectChanges();
  }

  private isVariant(variant: string): boolean {
    return (this.variant || '').includes(variant) || false;
  }
}
